import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, ChevronLeft, FileText, Check, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
const STEPS = [{
  id: 1,
  title: 'Property & Tenant'
}, {
  id: 2,
  title: 'Terms & Rent'
}, {
  id: 3,
  title: 'Clauses'
}, {
  id: 4,
  title: 'Review'
}];
export function LeaseWizard() {
  const {
    toast
  } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [formData, setFormData] = useState({
    propertyId: '',
    tenantName: '',
    startDate: '',
    endDate: '',
    rentAmount: '',
    clauses: {
      pets: false,
      smoking: false,
      subletting: false
    }
  });
  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleGenerate();
    }
  };
  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      toast.success('Lease agreement generated successfully');
    }, 2000);
  };
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <div className="space-y-6" data-id="element-1311">
            <div data-id="element-1312">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1313">
                Select Property
              </label>
              <select className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-1314">
                <option data-id="element-1315">Sunrise Apartments, Flat 4B</option>
                <option data-id="element-1316">Green Valley Estate, House 10</option>
              </select>
            </div>
            <div data-id="element-1317">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1318">
                Tenant Name
              </label>
              <input type="text" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Full legal name" data-id="element-1319" />
            </div>
          </div>;
      case 2:
        return <div className="space-y-6" data-id="element-1320">
            <div className="grid grid-cols-2 gap-4" data-id="element-1321">
              <div data-id="element-1322">
                <label className="block text-sm font-medium text-gray-700" data-id="element-1323">
                  Start Date
                </label>
                <input type="date" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1324" />
              </div>
              <div data-id="element-1325">
                <label className="block text-sm font-medium text-gray-700" data-id="element-1326">
                  End Date
                </label>
                <input type="date" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1327" />
              </div>
            </div>
            <div data-id="element-1328">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1329">
                Monthly Rent (₦)
              </label>
              <input type="number" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="150,000" data-id="element-1330" />
            </div>
          </div>;
      case 3:
        return <div className="space-y-4" data-id="element-1331">
            <h3 className="text-sm font-medium text-gray-900" data-id="element-1332">
              Standard Clauses
            </h3>
            <div className="space-y-3" data-id="element-1333">
              {['No Pets Allowed', 'No Smoking Indoors', 'No Subletting without Consent', 'Tenant pays Utilities'].map((clause, idx) => <div key={idx} className="flex items-center" data-id="element-1334">
                  <input id={`clause-${idx}`} type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-1335" />
                  <label htmlFor={`clause-${idx}`} className="ml-2 block text-sm text-gray-700" data-id="element-1336">
                    {clause}
                  </label>
                </div>)}
            </div>
            <div data-id="element-1337">
              <label className="block text-sm font-medium text-gray-700 mt-4" data-id="element-1338">
                Additional Custom Clauses
              </label>
              <textarea rows={3} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Enter any specific terms..." data-id="element-1339" />
            </div>
          </div>;
      case 4:
        return <div className="text-center py-8" data-id="element-1340">
            <div className="mx-auto h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4" data-id="element-1341">
              <FileText className="h-6 w-6 text-blue-600" data-id="element-1342" />
            </div>
            <h3 className="text-lg font-medium text-gray-900" data-id="element-1343">
              Ready to Generate
            </h3>
            <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto" data-id="element-1344">
              Our AI will now compile a legally sound lease agreement based on
              your inputs. You can review and edit before sending.
            </p>
          </div>;
      default:
        return null;
    }
  };
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-1345">
      {/* Progress Bar */}
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-1346">
        <div className="flex items-center justify-between" data-id="element-1347">
          {STEPS.map((step, idx) => <div key={step.id} className="flex items-center" data-id="element-1348">
              <div className={`flex items-center justify-center h-8 w-8 rounded-full border-2 ${currentStep > step.id ? 'bg-blue-600 border-blue-600 text-white' : currentStep === step.id ? 'border-blue-600 text-blue-600' : 'border-gray-300 text-gray-400'}`} data-id="element-1349">
                {currentStep > step.id ? <Check className="h-4 w-4" data-id="element-1350" /> : step.id}
              </div>
              <span className={`ml-2 text-sm font-medium hidden sm:block ${currentStep === step.id ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-1351">
                {step.title}
              </span>
              {idx < STEPS.length - 1 && <div className="w-12 h-0.5 bg-gray-200 mx-4 hidden sm:block" data-id="element-1352" />}
            </div>)}
        </div>
      </div>

      <div className="p-6 min-h-[300px]" data-id="element-1353">
        <motion.div key={currentStep} initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -20
      }} transition={{
        duration: 0.3
      }} data-id="element-1354">
          {renderStepContent()}
        </motion.div>
      </div>

      <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between" data-id="element-1355">
        <button onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))} disabled={currentStep === 1} className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50" data-id="element-1356">
          Back
        </button>
        <button onClick={handleNext} disabled={isGenerating} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50" data-id="element-1357">
          {isGenerating ? <>
              <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" data-id="element-1358" />
              Generating...
            </> : <>
              {currentStep === 4 ? 'Generate Lease' : 'Next Step'}
              {currentStep !== 4 && <ChevronRight className="ml-2 -mr-1 h-4 w-4" data-id="element-1359" />}
            </>}
        </button>
      </div>
    </div>;
}